// @flow

import React from "react";
import { localize } from "redux-i18n";
import PropTypes from "prop-types";
import { Pagination as PaginationReactStrap, PaginationItem, PaginationLink } from "reactstrap";
import { Icons } from "@components";

import "./pagination.style.scss";

/**
 *    Absolutely required props
 *    currentPage: number
      totalPages: number --> {Math.ceil(totalElements /pageSize)}
      onChange: function --> the argument is the number of the clicked page

 */

type StateProps = $Exact<{ inputPageNum: number }>;

type Props = $Exact<{
  currentPage: number,
  totalPages: number,
  className?: string,
  onChange: Function,
  ...PropsFromLocalize,
}>;

class Pagination extends React.Component<Props, StateProps> {
  state = {
    inputPageNum: 0,
  };

  get currentPage(): number {
    return this.props.currentPage || 1;
  }

  get totalPages(): number {
    return this.props.totalPages || 0;
  }

  paginationOnClickHandler = (e: Object) => {
    const itemDatasetVal = e.currentTarget.dataset.page;

    if (!isNaN(itemDatasetVal)) {
      this.props.onChange(itemDatasetVal);
    }
  };

  pageInputOnChange = (e: Object) => {
    this.setState({
      ...this.state,
      inputPageNum:
        e.currentTarget.value > this.totalPages ? this.totalPages : e.currentTarget.value,
    });
    if (e.charCode === 13 && this.state.inputPageNum) {
      this.goToPageHandler();
    }
  };

  goToPageHandler = () => {
    const num = this.state.inputPageNum;
    if (num <= this.totalPages || num !== this.currentPage) {
      this.props.onChange(num);
      this.setState({
        ...this.state,
        inputPageNum: 0,
      });
    }
  };

  get generatePageLinks() {
    const totalPages = this.totalPages;

    let totalElemToGenerate = 7; // The total ammount of pagination button elements to be generated (without the Left and Right arrows)
    let startPage = 1; // at the begining we start the render at page 1
    let pageLinks = [];
    let prependEllipsis = false;

    if (totalPages <= 1) return [];

    if (totalPages < 10) {
      // If the pages are not too many we don't need ellipsis buttons fancy logic
      pageLinks = [...Array.from({ length: totalPages }, (_, i) => i + startPage)];
    } else {
      pageLinks.push(1); // Always insert the first page

      if (this.currentPage > 5) {
        startPage = this.currentPage - 4;
        pageLinks.push("...");
      }

      if (this.currentPage + Math.ceil(totalElemToGenerate / 2) >= this.totalPages) {
        totalElemToGenerate = this.totalPages - this.currentPage + 4;
      }

      pageLinks = [
        ...pageLinks,
        ...Array.from({ length: totalElemToGenerate }, (_, i) => i + (startPage + 1)),
      ];

      if (this.currentPage <= totalPages - 5) {
        pageLinks.push("...");
        pageLinks.push(totalPages);
      }
    }

    return pageLinks;
  }

  render() {
    let generatedPageLinks = this.generatePageLinks;

    if (generatedPageLinks.length <= 1) {
      return null;
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <PaginationReactStrap className={`mt-0  ${this.props.className || ""}`}>
          <PaginationItem disabled={this.currentPage === 1}>
            <PaginationLink
              onClick={this.currentPage !== 1 ? this.paginationOnClickHandler : () => {}}
              data-page={this.currentPage - 1}
            >
              <Icons.ArrowRightIcon rotate={180} />
            </PaginationLink>
          </PaginationItem>

          {generatedPageLinks.map((page, idx) => (
            <PaginationItem key={`${page}-${idx}`} active={this.currentPage === page}>
              <PaginationLink onClick={this.paginationOnClickHandler} data-page={page}>
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem disabled={this.currentPage === this.totalPages}>
            <PaginationLink
              onClick={this.paginationOnClickHandler}
              data-page={this.currentPage + 1}
            >
              <Icons.ArrowRightIcon />
            </PaginationLink>
          </PaginationItem>
        </PaginationReactStrap>
        <div
          className={`ml-3 justify-content-center align-items-center ${
            this.totalPages >= 10 ? "d-flex" : "d-none"
          }`}
        >
          <span className="mr-2">{this.props.t("GO_TO_PAGE")}</span>
          <input
            type="number"
            className="py-1"
            min={1}
            max={this.totalPages}
            value={this.state.inputPageNum || ""}
            onChange={this.pageInputOnChange}
            onKeyPress={this.pageInputOnChange}
          />
          <button
            disabled={this.state.inputPageNum < 1}
            className="button small d-flex justify-content-center align-items-center ml-2"
            onClick={this.goToPageHandler}
          >
            {this.props.t("GO")}
          </button>
        </div>
      </div>
    );
  }
}

Pagination.propTypes = {
  onChange: PropTypes.func,

  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};

export default localize()(Pagination);
